import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { filter, tap } from 'rxjs/operators';

import { NgUtils } from 'tiime-expert-utils';
import {
  TiimeFormModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeOptionModule,
  TiimeOptionSelectionChange,
  TiimeSelectModule
} from 'tiime-material';

import { SelectorBase } from '@bases/selector/selector.base';
import { PcgEntry } from '@models/pcg-entry';
import { PcgEntryHelper } from '@modules/core/helpers/pcg-entry.helper';
import { CreatePcgEntryDialogData } from '@modules/shared/dialogs/create-pcg-entry-dialog/create-pcg-entry-dialog-data';
import { CreatePcgEntryDialogService } from '@modules/shared/dialogs/create-pcg-entry-dialog/create-pcg-entry-dialog.service';
import { FormFocusObserveDirective } from '@modules/shared/directives/form-focus-observe/form-focus-observe.directive';
import { PcgEntryOptionComponent } from '@modules/shared/options/pcg-entry-option/pcg-entry-option.component';
import { PcgEntriesWithoutArchivedPipe } from '@modules/shared/pipes/pcg-entries-without-archived/pcg-entries-without-archived.pipe';

@Component({
  standalone: true,
  selector: 'app-centralizer-pcg-entry-select',
  templateUrl: './centralizer-pcg-entry-select.component.html',
  styleUrls: ['./centralizer-pcg-entry-select.component.scss'],
  imports: [
    FormFocusObserveDirective,
    PcgEntryOptionComponent,
    TiimeInputContainerModule,
    TiimeFormModule,
    TiimeSelectModule,
    ReactiveFormsModule,
    TiimeOptionModule,
    TiimeIconModule,
    PcgEntriesWithoutArchivedPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CentralizerPcgEntrySelectComponent extends SelectorBase<PcgEntry> {
  @Input() ctrl: UntypedFormControl;
  @Input() errorMessage: string;
  @Input('pcgEntries') set setPcgEntries(pcgEntries: PcgEntry[]) {
    if (pcgEntries) {
      this.selectorData = pcgEntries;
      this.filteredSelectorData = pcgEntries;
    }
  }
  @Input() label = '';
  @Input() required = false;

  @Output() readonly emptyOptionSelected: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly selectChange: EventEmitter<PcgEntry> = new EventEmitter<PcgEntry>();
  @Output() readonly pcgEntryCreated: EventEmitter<PcgEntry> = new EventEmitter<PcgEntry>();

  readonly trackById = NgUtils.trackById;

  constructor(private createPcgEntryDialogService: CreatePcgEntryDialogService) {
    super();
  }

  filterFunction(search: string): PcgEntry[] {
    return PcgEntryHelper.filterPcgEntries(search, this.selectorData);
  }

  selectEmptyOption(event: TiimeOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }
    this.emptyOptionSelected.emit();
  }

  emitSelectChange(event: TiimeOptionSelectionChange, pcgEntry: PcgEntry): void {
    if (!event.isUserInput) {
      return;
    }
    this.selectChange.emit(pcgEntry);
  }

  openCreateCentralizerPcgEntryDialog(event: TiimeOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }

    const data: CreatePcgEntryDialogData = {
      accountNumber: this.searchControl.value,
      pcgEntries: this.selectorData,
      withCentralizer: true
    };

    this.createPcgEntryDialogService
      .open({
        data
      })
      .afterClosed()
      .pipe(
        filter((pcgEntry: PcgEntry) => !!pcgEntry),
        tap((pcgEntry: PcgEntry) => this.pcgEntryCreated.emit(pcgEntry))
      )
      .subscribe();
  }
}
